// src/components/auth/components/LoginForm.tsx
import React, { useId, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FiUser, FiLock, FiEye, FiEyeOff, FiAlertCircle } from "react-icons/fi";
import SocialButtons from "./SocialButtons";
import ServiceIconsStrip from "./ServiceIconsStrip";
import { login } from "@/api/client";
import { verifySession } from "@/api/auth";
import { resolvePostLoginRoute } from "@/components/auth/postLoginRoute";

export default function LoginForm() {
  const navigate = useNavigate();

  const [showPwd, setShowPwd] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const usernameId = useId();
  const pwdId = useId();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    if (!username.trim() || !password) {
      setErr("Please enter your username and password.");
      return;
    }

    setLoading(true);
    try {
      const result = await login({ username, password });

      // Step 1 → OTP required: go to OTP page with credentials in route state
      if (result && result.step === "otp") {
        navigate("/otp", {
          replace: true,
          state: {
            username,
            password,
          },
        });
        return;
      }

      // Multiple roles → choose a portal before a session is issued
      if (result && result.step === "select-role") {
        navigate("/select-portal", {
          replace: true,
          state: {
            selectionToken: result.selectionToken,
            availableRoles: result.availableRoles,
          },
        });
        return;
      }

      // Step 2 (or non-OTP flow) → user is logged in, go to dashboard
      if (result && result.step === "done") {
        const user = await verifySession().catch(() => null);
        navigate(resolvePostLoginRoute(user), { replace: true });
        return;
      }

      // Fallback: if backend shape is unexpected
      const user = await verifySession().catch(() => null);
      navigate(resolvePostLoginRoute(user), { replace: true });
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Login failed. Please check your credentials.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
      {/* Error message */}
      {err && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <div className="leading-5">{err}</div>
        </div>
      )}

      {/* Username */}
      <div>
        <label
          htmlFor={usernameId}
          className="mb-1 block text-xs font-medium text-gray-700"
        >
          Username
        </label>
        <div className="relative">
          <FiUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            id={usernameId}
            type="text"
            placeholder="admin"
            className="w-full rounded-xl border border-gray-300 bg-white px-9 py-2 text-sm text-gray-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            required
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
          />
        </div>
      </div>

      {/* Password */}
      <div>
        <label
          htmlFor={pwdId}
          className="mb-1 block text-xs font-medium text-gray-700"
        >
          Password
        </label>
        <div className="relative">
          <FiLock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            id={pwdId}
            type={showPwd ? "text" : "password"}
            placeholder="••••••••"
            className="w-full rounded-xl border border-gray-300 bg-white px-9 py-2 pr-10 text-sm text-gray-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowPwd((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-md text-gray-600 hover:bg-gray-100"
            aria-label={showPwd ? "Hide password" : "Show password"}
            tabIndex={-1}
          >
            {showPwd ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
      </div>

      {/* forgot */}
      <div className="flex justify-end">
        <Link
          to="/forgot-password"
          className="text-xs font-medium text-sky-600 hover:text-sky-700"
        >
          Forgot your password?
        </Link>
      </div>

      {/* login button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-xl bg-sky-500 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? "Signing in…" : "LOGIN"}
      </button>

      {/* divider */}
      <div className="my-2 flex items-center gap-3">
        <div className="h-px w-full bg-gray-200" />
        <span className="text-[10px] uppercase tracking-wider text-gray-400">
          OR
        </span>
        <div className="h-px w-full bg-gray-200" />
      </div>

      <SocialButtons />
      <ServiceIconsStrip />
    </form>
  );
}
